import {inject, Injectable, signal} from '@angular/core';
import {ActivatedRoute, Router} from "@angular/router";
import {firstValueFrom, from, Observable, switchMap, tap, throwError} from "rxjs";
import {Pronouns, PronounsIncludingYou, PronounsType} from "../../../model/pronouns";
import {Gender, GenderType} from "../../../model/gender";
import {SelectWithOptionValue} from "../../../components/select-with-other/select-with-other.component";
import {FlowDraftService} from "../../../services/flow-draft.service";
import {FormBuilder, ValidatorFn, Validators} from "@angular/forms";
import {Relation} from "../../../model/relation";
import {UserService} from "../../../services/user.service";
import {DisabilityInfoDto, HouseholdMemberPersonalInfoUpdateDto} from "homey-housing-lottery-app-dto";
import {generateRandom8DigitNumber} from "../../../utils";
import {NewLotteriesAfterUserUpdateService} from "../../../services/new-lotteries-after-user-update.service";
import {AnalyticsService} from "../../../services/analytics.service";
import {hasProblemWithHouseholdMembers, PostSignupUser} from "../../../model/user";

const olderThan18Validator: ValidatorFn = (control) => {
  const date = control.value as Date | undefined;
  if (date == undefined) {
    return null;
  }

  const today = new Date();
  const age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();

  if (age < 18 || (age === 18 && monthDiff < 0)) {
    return {olderThan18: true};
  }

  return null;
}

const bornAfter1900Validator: ValidatorFn = (control) => {
  const date = control.value as Date | undefined;
  if (date == undefined) {
    return null;
  }

  const year = date.getFullYear();
  if (year < 1900) {
    return {bornAfter1900: true};
  }

  return null;
}

interface PersonalInfo {
  readonly relation: Relation;
  readonly firstName: string;
  readonly lastName: string;
  readonly birthdate: string;
  readonly pronouns: Pronouns;
  readonly gender: Gender;
}

type IdInfo = {
  id: string,
  type: 'server'
} | {
  tempId: number,
  type: 'temp'
};

export interface FormHouseholdMember {
  personalInfo: PersonalInfo;
  accommodationDescription: string | undefined;
  hearingVisionDisability: boolean;
  needReasonableAccommodation: boolean;
  mobilityDisability: boolean;
  student: boolean;
  nycEmployee: boolean | undefined;
  id: IdInfo;
  userId: string | undefined;
}

export type FormCurrentHouseholdMember = Readonly<{
  personalInfo?: PersonalInfo
  mobilityDisability?: boolean;
  hearingVisionDisability?: boolean;
  needReasonableAccommodation?: boolean;
  accommodationDescription?: string;
  student?: boolean;
  nycEmployee: boolean | undefined;
  memberType: 'you' | 'other';
  id: IdInfo;
  userId: string | undefined;
}>;

@Injectable({
  providedIn: 'root'
})
export class UpdateHouseholdMembersFormService {
  constructor(
    private readonly router: Router,
    private readonly userService: UserService,
    private readonly flowDraftService: FlowDraftService,
    private readonly fb: FormBuilder
  ) {
  }

  private readonly analyticsService = inject(AnalyticsService);

  private readonly newLotteriesAfterUserUpdateService = inject(
    NewLotteriesAfterUserUpdateService
  );

  private _currentHouseholdMember: FormCurrentHouseholdMember | undefined = undefined;

  public getCurrentHouseholdMember(): FormCurrentHouseholdMember {
    if (this._currentHouseholdMember === undefined)
      throw new Error("In waiting state");

    return this._currentHouseholdMember;
  }

  public hasCurrentHouseholdMember(): boolean {
    return this._currentHouseholdMember !== undefined;
  }

  private _currentHouseholdMembers: FormHouseholdMember[] | undefined = undefined;
  private continueToIncomeFlowAfterReview = false;

  public getCurrentHouseholdMembers(): FormHouseholdMember[] {
    if (this._currentHouseholdMembers === undefined) {
      throw new Error("In waiting state");
    }

    return this._currentHouseholdMembers;
  }

  private async setCurrentHouseholdMember(value: FormCurrentHouseholdMember) {
    this._currentHouseholdMember = value;
    await this.flowDraftService.save('household-member', value);
  }

  public getHouseholdMembersEditRoute(): string[] {
    return ['/profile', 'update', 'household-info', '2', 'household'];
  }

  private async setCurrentHouseholdMembers(value: FormHouseholdMember[]) {
    this._currentHouseholdMembers = value;
    await this.flowDraftService.save('household-members', value);
  }

  public waitUntilDataLoaded = async () => {
    const user = await firstValueFrom(this.userService.getCurrentUserAfterSignupFlow$());
    if (this._currentHouseholdMember === undefined)
      {
        const storedCurrentHouseholdMember = await this.flowDraftService.get('household-member');

        if (storedCurrentHouseholdMember !== undefined) {
          if(storedCurrentHouseholdMember.userId !== undefined && storedCurrentHouseholdMember.userId !== user.id) {
            await this.flowDraftService.delete('household-member');
          } else {
            this._currentHouseholdMember = storedCurrentHouseholdMember;
          }
        }
      }

    if (this._currentHouseholdMembers === undefined) {
      this.newLotteriesAfterUserUpdateService.storeOldData();
      const householdMembersFromUser: FormHouseholdMember[] = user.householdInfo.members.map(
        member => ({
          personalInfo: {
            relation: member.relation,
            firstName: member.firstName,
            lastName: member.lastName,
            birthdate: member.birthdate,
            pronouns: member.pronouns,
            gender: member.gender,
          },
          student: member.student,
          nycEmployee: member.nycEmployee,
          mobilityDisability: member.disabilityInfo.mobility,
          hearingVisionDisability: member.disabilityInfo.hearingVision,
          needReasonableAccommodation: member.disabilityInfo.needsAccommodation,
          accommodationDescription: member.disabilityInfo.needsAccommodation ? member.disabilityInfo.accommodationDescription : undefined,
          id: {
            id: member.id,
            type: 'server'
          },
          userId: user.id
        }));

      const storedMembers = await this.flowDraftService.get('household-members');
      const members = storedMembers?.filter(
        member => member.userId === undefined || member.userId === user.id
      ) ?? householdMembersFromUser;

      await this.setCurrentHouseholdMembers(members);
    }

    return true;
  }

  public createHouseholdMemberDetailsForm() {
    const currentHouseholdMember = this.getCurrentHouseholdMember();
    const personalInfo = currentHouseholdMember?.personalInfo;
    const dateString = personalInfo?.birthdate;
    const date = dateString ? new Date(dateString) : undefined;

    const birthDateValidator = [
      Validators.required,
      bornAfter1900Validator
    ];

    if (currentHouseholdMember?.memberType === 'you') {
      birthDateValidator.push(olderThan18Validator);
    }

    return signal(this.fb.group({
      relation: this.fb.nonNullable.control<Relation | undefined>(currentHouseholdMember?.memberType === 'you' ? 'you' : (personalInfo?.relation ?? personalInfo?.relation), {validators: currentHouseholdMember?.memberType === 'you' ? [] : [Validators.required]}),
      firstName: this.fb.nonNullable.control(personalInfo?.firstName ?? '', {validators: [Validators.required]}),
      lastName: this.fb.nonNullable.control(personalInfo?.lastName ?? '', {validators: [Validators.required]}),
      birthdate: this.fb.nonNullable.control<Date | undefined>(date, {validators: birthDateValidator}),
      gender: this.fb.nonNullable.control<SelectWithOptionValue<GenderType>>(personalInfo?.gender, {validators: [Validators.required]}),
      pronouns: this.fb.nonNullable.control<SelectWithOptionValue<PronounsType>>(personalInfo?.pronouns, {validators: [Validators.required]})
    }));
  }

  private updateCurrentHouseholdMember = async (update: Partial<FormCurrentHouseholdMember>) => {
    const newCurrentHouseholdMember: FormCurrentHouseholdMember = {
      ...this.getCurrentHouseholdMember(),
      ...update
    };

    await this.setCurrentHouseholdMember(newCurrentHouseholdMember);
  }

  getCurrentHouseholdMemberType() {
    return this.getCurrentHouseholdMember().memberType;
  }

  private mapPronouns = (pronouns: NonNullable<SelectWithOptionValue<PronounsType>>): Pronouns => {
    if (pronouns.type === 'other') {
      return {
        type: 'other',
        other: pronouns.other
      }
    }

    return {
      type: pronouns.type
    }
  }

  private mapGender = (gender: NonNullable<SelectWithOptionValue<GenderType>>): Gender => {
    if (gender.type === 'other') {
      return {
        type: 'other',
        other: gender.other
      }
    }

    return {
      type: gender.type
    }
  }

  submitPersonalInfo = (value: {
    relation: Relation | undefined,
    firstName: string,
    lastName: string,
    birthdate: Date | undefined,
    pronouns: SelectWithOptionValue<PronounsType>,
    gender: SelectWithOptionValue<GenderType>
  }, route: ActivatedRoute) => {
    if (value.pronouns === undefined)
      return throwError(() => new Error("Pronouns are required"));
    if (value.relation === undefined)
      return throwError(() => new Error("Relation is required"));
    if (value.gender === undefined)
      return throwError(() => new Error("Gender is required"));
    if (value.birthdate === undefined)
      return throwError(() => new Error("Birthdate is required"));

    return from(this.updateCurrentHouseholdMember({
      personalInfo: {
        relation: value.relation,
        firstName: value.firstName,
        lastName: value.lastName,
        birthdate: value.birthdate.toISOString(),
        pronouns: this.mapPronouns(value.pronouns),
        gender: this.mapGender(value.gender)
      }
    })).pipe(
      switchMap(() => this.router.navigate(['..', 'student'], {relativeTo: route}))
    );
  }

  submitHasMobilityDisability = (value: boolean, route: ActivatedRoute) => {
    return from(this.updateCurrentHouseholdMember({
      mobilityDisability: value
    })).pipe(
      switchMap(() => this.router.navigate(['..', 'vision-hearing-disability'], {relativeTo: route}))
    );
  }

  submitHasHearingVisionDisability = (value: boolean, route: ActivatedRoute) => {
    return from(this.updateCurrentHouseholdMember({
      hearingVisionDisability: value,
    })).pipe(
      switchMap(() => {
        if (this.getCurrentHouseholdMember().hearingVisionDisability || this.getCurrentHouseholdMember().mobilityDisability) {
          return this.router.navigate(['..', 'needs-accommodation'], {relativeTo: route});
        } else {
          return this.completeHouseholdMember(route);
        }
      })
    );
  }

  submitNeedReasonableAccommodation = (value: boolean, route: ActivatedRoute) => {
    return from(this.updateCurrentHouseholdMember({
      needReasonableAccommodation: value
    })).pipe(
      switchMap(() => {
        if (value) {
          return this.router.navigate(['..', 'accommodation-details'], {relativeTo: route});
        } else {
          return this.completeHouseholdMember(route);
        }
      })
    );
  }

  submitAccommodationDescription = (value: { description: string }, route: ActivatedRoute) => {
    return from(this.updateCurrentHouseholdMember({
      accommodationDescription: value.description
    })).pipe(
      switchMap(() => this.completeHouseholdMember(route))
    );
  }

  public async openHouseholdMember(route: ActivatedRoute, householdMember: FormHouseholdMember) {
    await this.setCurrentHouseholdMember({
      ...householdMember,
      memberType: householdMember.personalInfo.relation === 'you' ? 'you' : 'other',
      id: householdMember.id
    });

    await this.router.navigate(['../..', '1'], {relativeTo: route});
  }

  public async startAddHouseholdMember(relativeRoute: string[], route: ActivatedRoute) {
    const id: IdInfo = {
      tempId: generateRandom8DigitNumber(),
      type: 'temp'
    };

    if (this.getCurrentHouseholdMembers().length === 0) {
      await this.setCurrentHouseholdMember({
        memberType: 'you',
        nycEmployee: undefined,
        id: id,
        userId: this.userService.userId()
      });
    } else {
      await this.setCurrentHouseholdMember({
        memberType: 'other',
        nycEmployee: undefined,
        id: id,
        userId: this.userService.userId()
      })
    }
    await this.router.navigate(relativeRoute, {relativeTo: route});
  }

  public async startForm(route: ActivatedRoute) {
    this.analyticsService.sendEvent(
      'household-flow-started'
    );

    if (this.getCurrentHouseholdMembers().length === 0) {
      await this.startAddHouseholdMember(['..', '1'], route);
    } else {
      await this.router.navigate(['..', '2'], {relativeTo: route});
    }
  }

  public completeHouseholdMember(route: ActivatedRoute) {
    const currentHouseholdMember = this.getCurrentHouseholdMember();

    if (currentHouseholdMember?.mobilityDisability === undefined)
      return throwError(() => new Error("Mobility disability is required"));
    if (currentHouseholdMember?.hearingVisionDisability === undefined)
      return throwError(() => new Error("Hearing vision disability is required"));
    if (currentHouseholdMember?.personalInfo === undefined)
      return throwError(() => new Error("Personal info is required"));
    if (currentHouseholdMember?.student === undefined)
      return throwError(() => new Error("Student status is required"));

    let householdMember: FormHouseholdMember;
    if (currentHouseholdMember.needReasonableAccommodation && (currentHouseholdMember.hearingVisionDisability || currentHouseholdMember.mobilityDisability)) {
      if (currentHouseholdMember.accommodationDescription === undefined)
        return throwError(() => new Error("Accommodation description is required"));

      householdMember = {
        id: currentHouseholdMember.id,
        personalInfo: currentHouseholdMember.personalInfo,
        mobilityDisability: currentHouseholdMember.mobilityDisability,
        hearingVisionDisability: currentHouseholdMember.hearingVisionDisability,
        needReasonableAccommodation: currentHouseholdMember.needReasonableAccommodation,
        accommodationDescription: currentHouseholdMember.accommodationDescription,
        student: currentHouseholdMember.student,
        nycEmployee: currentHouseholdMember.nycEmployee,
        userId: this.userService.userId()
      };
    } else {
      householdMember = {
        id: currentHouseholdMember.id,
        personalInfo: currentHouseholdMember.personalInfo,
        mobilityDisability: currentHouseholdMember.mobilityDisability,
        hearingVisionDisability: currentHouseholdMember.hearingVisionDisability,
        needReasonableAccommodation: false,
        accommodationDescription: currentHouseholdMember.accommodationDescription,
        student: currentHouseholdMember.student,
        nycEmployee: currentHouseholdMember.nycEmployee,
        userId: this.userService.userId()
      }
    }

    let observable: Observable<unknown>;
    const index = this.getCurrentHouseholdMembers().findIndex(
      member => {
        switch (member.id.type) {
          case 'server':
            return currentHouseholdMember.id.type === 'server' && member.id.id === currentHouseholdMember.id.id;
          case 'temp':
            return currentHouseholdMember.id.type === 'temp' && member.id.tempId === currentHouseholdMember.id.tempId;
        }
      }
    );

    if (index === -1) {
      observable = from(this.setCurrentHouseholdMembers([
        ...this.getCurrentHouseholdMembers(),
        householdMember
      ]));
    } else {
      const members = [...this.getCurrentHouseholdMembers()];
      members[index] = householdMember;
      observable = from(this.setCurrentHouseholdMembers(members));
    }

    return observable.pipe(
      switchMap(() => this.router.navigate(['../..', '2'], {relativeTo: route}))
    );
  }

  public clearCurrentHouseholdMember() {
    this._currentHouseholdMember = undefined;
    this.flowDraftService.delete('household-member');
  }

  private formDisabilityInfoToDto = (formHouseholdMember: FormHouseholdMember): DisabilityInfoDto => {
    if (formHouseholdMember.needReasonableAccommodation && (formHouseholdMember.mobilityDisability || formHouseholdMember.needReasonableAccommodation)) {
      if (formHouseholdMember.accommodationDescription === undefined)
        throw new Error("Accommodation description is required");

      return {
        mobility: formHouseholdMember.mobilityDisability,
        hearingVision: formHouseholdMember.hearingVisionDisability,
        needsAccommodation: formHouseholdMember.needReasonableAccommodation,
        accommodationDescription: formHouseholdMember.accommodationDescription
      }
    } else {
      return {
        mobility: formHouseholdMember.mobilityDisability,
        hearingVision: formHouseholdMember.hearingVisionDisability,
        needsAccommodation: false
      }
    }
  }

  private formHouseholdMemberToPersonalInfoUpdateDto = (formHouseholdMember: FormHouseholdMember): HouseholdMemberPersonalInfoUpdateDto => {
    if (formHouseholdMember.personalInfo.relation === undefined)
      throw new Error("Relation is required");

    return {
      id: formHouseholdMember.id.type === 'server' ? formHouseholdMember.id.id : undefined,
      relation: formHouseholdMember.personalInfo.relation,
      firstName: formHouseholdMember.personalInfo.firstName,
      lastName: formHouseholdMember.personalInfo.lastName,
      birthdate: formHouseholdMember.personalInfo.birthdate,
      pronouns: formHouseholdMember.personalInfo.pronouns,
      gender: formHouseholdMember.personalInfo.gender,
      student: formHouseholdMember.student,
      nycEmployee: formHouseholdMember.nycEmployee,
      disabilityInfo: this.formDisabilityInfoToDto(formHouseholdMember)
    }
  }

  public submitHousehold(route: ActivatedRoute) {
    const previousMemberIds = new Set(
      this.userService.userAfterSignupFlow()?.householdInfo.members.map(member => member.id) ?? []
    );

    return this.userService.updateHouseholdMembers(
      {
        members: this.getCurrentHouseholdMembers().map(this.formHouseholdMemberToPersonalInfoUpdateDto),
      }
    ).pipe(
      tap(() => this.flowDraftService.delete('household-members')),
      tap(() => this._currentHouseholdMembers = undefined),
      tap(() => this.flowDraftService.delete('household-member')),
      tap(() => this._currentHouseholdMember = undefined),
      switchMap(() => this.userService.getCurrentUserAfterSignupFlow$()),
      switchMap((user) => {
        if (user.householdInfo.members.every(member => member.student)) {
          return this.router.navigate(['../..', 'all-students'], {relativeTo: route});
        } else {
          const householdMembersChanged = this.haveHouseholdMembersChanged(
            previousMemberIds,
            user.householdInfo.members.map(member => member.id)
          );
          return this.gotoCompletePage(route, householdMembersChanged);
        }
      })
    )
  }


  public gotoCompletePage(route: ActivatedRoute, householdMembersChanged: boolean) {
    this.analyticsService.sendEvent(
      'household-flow-success'
    );
    const user = this.userService.userAfterSignupFlow();

    const shouldStartIncomeFlow = user?.status === 'active' && householdMembersChanged;
    this.setContinueToIncomeFlowAfterReview(shouldStartIncomeFlow);
    return this.router.navigate(['../..', 'complete', 'review'], {
      relativeTo: route,
    })
  }

  private haveHouseholdMembersChanged(previousMemberIds: Set<string>, updatedMemberIds: string[]): boolean {
    const updatedMemberIdsSet = new Set(updatedMemberIds);
    const memberAdded = updatedMemberIds.some(id => !previousMemberIds.has(id));
    const memberRemoved = Array.from(previousMemberIds).some(id => !updatedMemberIdsSet.has(id));
    return memberAdded || memberRemoved;
  }

  public consumeContinueToIncomeFlowAfterReview(): boolean {
    const value = this.continueToIncomeFlowAfterReview;
    this.continueToIncomeFlowAfterReview = false;
    return value;
  }

  private setContinueToIncomeFlowAfterReview(value: boolean) {
    this.continueToIncomeFlowAfterReview = value;
  }

  public getCurrentHouseholdMemberName(): string | undefined {
    return this.getCurrentHouseholdMember().personalInfo?.firstName;
  }

  public getCurrentHouseholdMemberPronouns(): PronounsIncludingYou {
    if (this.getCurrentHouseholdMember().memberType === 'you')
      return {type: 'you'};

    return this.getCurrentHouseholdMember().personalInfo?.pronouns ?? {type: 'they'};
  }

  deleteHouseholdMember(member: FormHouseholdMember) {
    const members = [...this.getCurrentHouseholdMembers()];
    const index = members.indexOf(member);

    if (index === -1)
      throw new Error("Member not found");

    members.splice(index, 1);
    this.setCurrentHouseholdMembers(members);
  }

  submitStudentStatus(student: boolean, route: ActivatedRoute) {
    return from(this.updateCurrentHouseholdMember({
      student: student
    })).pipe(
      switchMap(() => this.router.navigate(['..', 'nyc-employee'], {relativeTo: route}))
    );
  }

  submitNycEmployeeStatus(nycEmployee: boolean, route: ActivatedRoute) {
    return from(this.updateCurrentHouseholdMember({
      nycEmployee: nycEmployee
    })).pipe(
      switchMap(() => this.router.navigate(['..', 'mobility-disability'], {relativeTo: route}))
    );
  }
}

type SectionStatus = 'not-started' | 'recommended' | 'complete' | 'problem';

export const getHouseholdMembersStatus = (user: PostSignupUser): SectionStatus => {
  if (hasProblemWithHouseholdMembers(user.householdInfo.members)) {
    return 'problem';
  }

  if (user.householdInfo.members.length > 0) {
    return 'complete';
  }

  return user.addressInfo?.status === 'complete' ? 'recommended' : 'not-started';
};

export const isHouseholdSectionComplete = (user?: PostSignupUser): boolean => {
  return !!user && getHouseholdMembersStatus(user) === 'complete';
};
